import { ChangeDetectionStrategy, Component, Input } from '@angular/core'
import { ConfigStore } from '@engineering11/config-web'
import { ConversationStore } from '@engineering11/messaging-web'

/**
 * The three conversation-type groups shown under Messages.
 *
 * One look in both places (CNCT-6775): no filled cards, each conversation type separated by the
 * nav's standard sub-group divider (mirrors .nav-subgroup in app-nav-item), and the per-conversation
 * expand toggles sized to match .nav-chevron. The `variant` input now only covers what genuinely
 * differs between the two surfaces:
 *   - `panel` (default) — the desktop slide-out messaging column, which is its own scroll container.
 *   - `inline` — the below-lg nav drawer, where these sit inside `custom-messaging-nav`'s open
 *     .nav-group card and the DRAWER does the scrolling, so every nested scroller is disabled.
 *
 * Loading state: `sdk-conversation-group-nav` has a loader of its own, but it sits INSIDE an
 * `*ngrxLet` over the grouped-conversations query, and `LetDirective` renders nothing at all before
 * its source first emits (no suspense template = `viewContainerRef.clear()`). So on a cold load that
 * component contributes an empty DOM. Gate the groups on `getConversationsLoaded` and show one
 * loading row for the whole section until the conversations are in. The row is styled from this
 * component's own CSS rather than a Tailwind colour class passed into a library component, so it
 * cannot come out invisible.
 */
@Component({
  selector: 'messaging-nav-groups',
  template: `
    <div class="nav-messages-groups e11-flex e11-flex-col" [ngClass]="{ 'nav-messages-inline': isInline }">
      <ng-container *ngIf="conversationsLoaded$ | async; else loading">
        <div class="nav-message-group">
          <ng-container *ngTemplateOutlet="groupHeader; context: { title: 'Application', subtitle: 'Direct Messages' }"></ng-container>
          <nav-item-direct-messages></nav-item-direct-messages>
        </div>
        <div class="nav-message-group" *ngIf="(features$ | async)?.jobApplicationChannels">
          <ng-container *ngTemplateOutlet="groupHeader; context: { title: 'Application', subtitle: 'Group Discussion' }"></ng-container>
          <nav-item-job-application-messages></nav-item-job-application-messages>
        </div>
        <div class="nav-message-group">
          <ng-container *ngTemplateOutlet="groupHeader; context: { title: 'Candidate Pool', subtitle: 'Direct Messages' }"></ng-container>
          <nav-item-candidate-pool-messages></nav-item-candidate-pool-messages>
        </div>
      </ng-container>

      <ng-template #loading>
        <div class="nav-messages-loading" role="status">
          <span class="nav-messages-loading-dot"></span>
          <span class="nav-messages-loading-dot"></span>
          <span class="nav-messages-loading-dot"></span>
          <span class="nav-messages-loading-text">{{ 'Loading conversations' | translate }}</span>
        </div>
      </ng-template>

      <ng-template #groupHeader let-title="title" let-subtitle="subtitle">
        <div class="e11-flex e11-font-medium e11-justify-between e11-items-center e11-mb-2 e11-px-2 e11-text-skin-white">
          <p class="e11-text-md e11-mb-0">{{ title | translate }}</p>
          <p class="e11-text-xs e11-mb-0">({{ subtitle | translate }})</p>
        </div>
      </ng-template>
    </div>
  `,
  styles: [
    `
      /* CNCT-6905: a custom element is inline by default, and this one wraps a block-level flex
         column. WebKit (Safari, and WKWebView-based browsers) lays that out as an inline
         "continuation" and did not re-lay it out when the loading row went in, so below lg the
         opened Messages section stayed 0px tall — no loading row — until the conversations
         arrived and forced a relayout. Chrome never showed it. Make the host a real block. */
      :host {
        display: block;
      }

      .nav-messages-inline {
        padding: 0 4px 6px;
      }

      /* Divider above each conversation type — the same rule app-nav-item's .nav-subgroup uses
         between Website / Personnel / Settings, so the three read as one section rather than
         three islands. The topmost one also separates the groups from whatever sits above. */
      .nav-message-group {
        width: 100%;
        border-top: 1px solid rgba(255, 255, 255, 0.18);
        margin-top: 4px;
        padding-top: 8px;
      }

      /* Inline, the topmost divider separates the groups from the Messages row above it. In the
         desktop panel there is nothing above, so a rule across the top reads as a stray line. */
      .nav-messages-groups:not(.nav-messages-inline) .nav-message-group:first-child {
        border-top: 0;
        margin-top: 0;
        padding-top: 0;
      }

      /* Per-conversation expand toggles ship as a bare .material-icons-outlined — 24px, and full
         white via the expander's e11-text-white container. Bring them to the nav's own chevron
         (.nav-chevron in app-nav-item): 18px at 55% white. */
      .nav-messages-groups ::ng-deep .e11-nav-expander-indicator {
        font-size: 18px;
        color: rgba(255, 255, 255, 0.55);
      }
      .nav-messages-groups ::ng-deep .e11-nav-expander-container:hover .e11-nav-expander-indicator {
        color: rgba(255, 255, 255, 0.85);
      }

      /* The expander row ships with a hover fill but no radius, so the hover state renders as a
         hard-edged block against rounded neighbours. Match sdk-conversation-nav-item's rounded-md. */
      .nav-messages-groups ::ng-deep .e11-nav-expander-container {
        border-radius: 6px;
        font-size: 15px;
      }

      /* Unread dot: the library places it absolutely at left: -2px, leaving ~4px before the title.
         Nudge it inside the row and open the title's indent to a full 8px of clearance. */
      .nav-messages-groups ::ng-deep .e11-nav-expander-container e11-indicator .e11-indicator {
        left: 2px !important;
      }
      .nav-messages-groups ::ng-deep .e11-nav-expander-container e11-indicator + div {
        padding-left: 16px;
      }

      /* Inline only: the nav drawer is the scroll container, but sdk-conversation-group-nav caps
         its list at 75vh and e11-nav-expander's open panel at 500px, each with overflow-y: auto.
         Nested inside the drawer that is a scrollbar within a scrollbar, so let both run to their
         natural height. The desktop panel is its own scroll container and keeps the defaults. */
      .nav-messages-inline ::ng-deep sdk-conversation-group-nav > ul {
        max-height: none;
        overflow-y: visible;
      }
      .nav-messages-inline ::ng-deep .e11-nav-expander.e11-nav-expander-active {
        max-height: 2000px;
        overflow-y: visible;
      }

      /* The row is a column flex item, so it will be squashed to the height of its dots unless it
         refuses to shrink — which is exactly what it did: ~10px tall with the padding collapsed
         away. min-height holds the row at a readable size for the whole load. */
      .nav-messages-loading {
        display: flex;
        align-items: center;
        flex-shrink: 0;
        min-height: 56px;
        gap: 5px;
        padding: 18px 8px;
        color: rgba(255, 255, 255, 0.55);
        font-size: 13px;
      }
      .nav-messages-loading-dot {
        flex-shrink: 0;
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.55);
        animation: nav-messages-loading-pulse 1.4s infinite ease-in-out;
      }
      .nav-messages-loading-dot:nth-child(2) {
        animation-delay: 0.2s;
      }
      .nav-messages-loading-dot:nth-child(3) {
        animation-delay: 0.4s;
      }
      .nav-messages-loading-text {
        margin-left: 5px;
      }
      @keyframes nav-messages-loading-pulse {
        0%,
        100% {
          opacity: 0.3;
        }
        50% {
          opacity: 1;
        }
      }
      @media (prefers-reduced-motion: reduce) {
        .nav-messages-loading-dot {
          animation: none;
          opacity: 0.7;
        }
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MessagingNavGroupsComponent {
  features$ = this.configStore.features$
  conversationsLoaded$ = this.conversationStore.getConversationsLoaded

  @Input() variant: 'panel' | 'inline' = 'panel'

  get isInline(): boolean {
    return this.variant === 'inline'
  }

  constructor(private configStore: ConfigStore, private conversationStore: ConversationStore) {}
}
